import React, { FC, useEffect } from 'react';
//import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isInternalDomain } from 'src/api';
import {
  selectApiKey,
  selectEntitlementsUrl,
  selectEnvironment,
  selectLoginUrl,
  selectLogoutUrl,
  selectVersion,
} from 'src/state/app/appConfigSlice';
import { saveGlobalAuthDelegateUser } from 'src/state/authSlice/authSlice';
import { EEnvironments } from 'utils/constants';
import { initFaro } from 'utils/faro';
import { registerBeaconComponents } from '@trp-beacon-ui-kit/foundation';
import { AuthGate, AuthServiceProvider } from '@trp-ta-nitro/auth-react';
import { useOtelContext } from '@trp-ta-nitro/otel-react';
import App from './App';

registerBeaconComponents();

/**
 * A wrapper around the App that makes sure the auth service is properly available
 * using the AuthServiceProvider component.
 *
 * @constructor
 */
const AuthenticatedApp: FC = () => {
  const context = useOtelContext();

  // the auth options here lean heavily on the defaults, any other configuration could be
  // pulled in:
  //   - static configuration here
  //   - via the app state (where the apiKey is currently coming from)
  //   - a separate fetch here to specific config

  const dispatch = useDispatch();
  const apiKey = useSelector(selectApiKey);
  const entitlementsUrl = useSelector(selectEntitlementsUrl);
  const loginUrl = useSelector(selectLoginUrl);
  const logoutUrl = useSelector(selectLogoutUrl);
  const environment = useSelector(selectEnvironment);
  const version = useSelector(selectVersion);

  useEffect(() => {
    if (environment) {
      initFaro(environment, version || '1.0.0');
    }
  }, [environment, version]);

  if (isInternalDomain()) {
    dispatch(
      saveGlobalAuthDelegateUser({
        customerWebView: true,
      }),
    );
  }

  const authOptions = {
    apiKey,
    types: {
      // enable external and internal both, no extra options are required so none are passed
      external: {
        apiKey,
        authUrl: entitlementsUrl,
        baseDomain: 'troweprice.com',
        environment:
          environment === EEnvironments.SANDBOX ? EEnvironments.DEV : environment,
        loginUrl,
        logoutUrl,
        requireAuthentication: true,
      },
      internal: {
        customerWebViewEnabled: true,
      },
    },
  };

  return (
    <AuthServiceProvider context={context} options={authOptions}>
      <AuthGate>
        <App />
      </AuthGate>
    </AuthServiceProvider>
  );
};

export default AuthenticatedApp;
